// src/contexts/SurveyContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  fetchSurveys as fetchAllSurveys,
  fetchSurveyById,
} from "../services/surveyService";
import { useAuth } from "./AuthContext"; // Import useAuth to check authentication status

const SurveyContext = createContext();

const SurveyProvider = ({ children }) => {
  const { isAuthenticated } = useAuth(); // Access isAuthenticated from AuthContext
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false); // Default to false to avoid initial fetch
  const [error, setError] = useState(null);

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllSurveys();
      setSurveys(data?.surveys || []);
    } catch (err) {
      console.error("Error fetching surveys:", err);
      setError("Failed to fetch surveys. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const selectSurvey = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSurveyById(id);
      return data; // Return the survey data
    } catch (err) {
      console.error("Error fetching survey details:", err);
      setError("Failed to fetch survey details. Please try again.");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      if (!surveys.length) { // Prevent fetching if surveys are already loaded
        fetchSurveys();
      }
    }
  }, [isAuthenticated, fetchSurveys, surveys.length]);
  

  const value = useMemo(
    () => ({
      surveys,
      loading,
      error,
      fetchSurveys,
      selectSurvey,
    }),
    [surveys, loading, error, fetchSurveys, selectSurvey]
  );

  return <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>;
};

export const useSurvey = () => useContext(SurveyContext);
export default SurveyProvider;
